import { CardCategory, normalizeCardCategory } from '@supply-mover-2-0/shared';
import { PaymentMethodTypeEnum } from '../types/payment.types';

/**
 * PD-16381 — the type name a payment or saved payment method is shown with.
 *
 * Every card is stored with the payment type 'Credit Card', whatever it
 * actually is, so labelling a card by its stored type called debit cards and
 * cards nobody has classified "Credit Card" — a customer paying with a debit
 * card saw a receipt line claiming otherwise. A card is therefore named by how
 * it is funded; anything that is not a card keeps its own type ('ACH',
 * 'Account Funds', 'Check').
 *
 * These return untranslated English keys; every caller passes the result
 * through `t()` (the server-rendered documents print them as-is).
 */

/**
 * The stored payment types that are a card. A card-reader payment is a card
 * presented in person, so it is named the same way.
 */
const CARD_PAYMENT_TYPES: ReadonlySet<string> = new Set([
	PaymentMethodTypeEnum.CREDIT_CARD,
	PaymentMethodTypeEnum.CREDIT_CARD_READER,
]);

/** Whether a stored payment type is a card. */
export function isCardPaymentType(type: string | null | undefined): boolean {
	return type != null && CARD_PAYMENT_TYPES.has(type);
}

/**
 * A card's type name from its funding: "Credit Card", "Debit Card",
 * "Prepaid Card", or plain "Card" for anything else — a charge or
 * deferred-debit card, one the gateway could not classify, or one never
 * asked about. Plain "Card" rather than "Credit Card" because claiming a
 * funding type nobody has established is the error this exists to stop.
 */
export function cardTypeLabel(cardCategory: string | null | undefined): string {
	switch (normalizeCardCategory(cardCategory)) {
		case CardCategory.CREDIT:
			return 'Credit Card';
		case CardCategory.DEBIT:
			return 'Debit Card';
		case CardCategory.PREPAID:
			return 'Prepaid Card';
		default:
			return 'Card';
	}
}

/**
 * The type name for a payment or saved method: a card by its funding
 * (`cardTypeLabel`), anything else by its own stored type. Empty when there
 * is no type at all, so the caller can supply its own fallback.
 */
export function paymentTypeLabel(
	type: string | null | undefined,
	cardCategory: string | null | undefined,
): string {
	if (isCardPaymentType(type)) {
		return cardTypeLabel(cardCategory);
	}

	return type ?? '';
}

/**
 * A payment's short name for a list row: "Visa ****4242", "Card ****4444",
 * "ACH ****6789", "Cash". The network name when known, else the type name
 * (a card by its funding), with the last four whenever they are known — a
 * card with no network name used to lose its digits here, while its detail
 * page and receipt still named them.
 */
export function formatTransactionMethodLabel(method: {
	subtype?: string | null;
	type: string | null | undefined;
	cardCategory?: string | null;
	lastFour?: string | null;
}): string {
	const subtype = method.subtype?.trim() ?? '';
	const name =
		subtype !== ''
			? subtype
			: paymentTypeLabel(method.type, method.cardCategory);
	const lastFour = method.lastFour?.trim() ?? '';

	return [name, lastFour !== '' ? `****${lastFour}` : '']
		.filter((part) => part !== '')
		.join(' ');
}
